/**
 * Utilitários para gerenciamento do carrinho
 */

import { SessionKeys, ItemCarrinho, TipoCarrinho, CarrinhoState, Produto, ProdutoPrevisaoVendedor } from '@types';
import { SessionUtil } from './session.util';

export class CarrinhoUtil {
  /**
   * Carrega carrinho da sessão
   */
  static loadCarrinho(tipo: TipoCarrinho): CarrinhoState {
    const saved = SessionUtil.get<string>(SessionKeys.CARRINHO);
    const savedTipo = SessionUtil.get<TipoCarrinho>(SessionKeys.CARRINHO_TIPO);

    if (saved && savedTipo === tipo) {
      try {
        // Se saved já é um objeto, não precisa fazer parse
        const data = typeof saved === 'string' ? JSON.parse(saved) : saved;

        // Verifica se tem a estrutura correta
        if (!data.items || !data.tipo) {
          console.warn('Carrinho com estrutura inválida, criando novo');
          return this.createEmptyCarrinho(tipo);
        }

        return {
          items: new Map(Object.entries(data.items)),
          tipo: data.tipo,
          total: data.total || 0,
          pesoTotal: data.pesoTotal || 0,
          quantidadeTotal: data.quantidadeTotal || 0,
        };
      } catch (error) {
        console.error('Erro ao carregar carrinho:', error);
        // Limpa dados corrompidos
        SessionUtil.remove(SessionKeys.CARRINHO);
        SessionUtil.remove(SessionKeys.CARRINHO_TIPO);
      }
    }

    return this.createEmptyCarrinho(tipo);
  }

  /**
   * Salva carrinho na sessão
   */
  static saveCarrinho(carrinho: CarrinhoState): void {
    try {
      const data = {
        items: Object.fromEntries(carrinho.items),
        tipo: carrinho.tipo,
        total: carrinho.total,
        pesoTotal: carrinho.pesoTotal,
        quantidadeTotal: carrinho.quantidadeTotal,
      };

      SessionUtil.set(SessionKeys.CARRINHO, JSON.stringify(data));
      SessionUtil.set(SessionKeys.CARRINHO_TIPO, carrinho.tipo);
    } catch (error) {
      console.error('Erro ao salvar carrinho:', error);
    }
  }

  /**
   * Cria carrinho vazio
   */
  static createEmptyCarrinho(tipo: TipoCarrinho): CarrinhoState {
    return {
      items: new Map(),
      tipo,
      total: 0,
      pesoTotal: 0,
      quantidadeTotal: 0,
    };
  }

  /**
   * Adiciona produto ao carrinho
   */
  static addItem(
    carrinho: CarrinhoState,
    produto: Produto,
    quantidade: number,
    precoEditado?: number
  ): CarrinhoState {
    const vendedor = SessionUtil.getVendedor();
    const cliente = SessionUtil.getCliente();

    if (!vendedor) {
      throw new Error('Vendedor não encontrado na sessão');
    }

    const peso = parseFloat(produto.peso) * quantidade;
    const precoUnitario = precoEditado || parseFloat(produto.precoVenda);
    const valor = precoUnitario; // Valor unitário do item
    const total = precoUnitario * quantidade; // Total = quantidade * valor

    // Determina se está usando estoque loja (1) ou estoque normal (0)
    // Para pedidos: loja = 1 se estoque === 0 e estoque_loja > 0, caso contrário loja = 0
    const usandoEstoqueLoja = carrinho.tipo === 'pedido' &&
                               (Number(produto.estoque) === 0 &&
                                Number(produto.estoque_loja) > 0);

    const item: ItemCarrinho = {
      codigo: produto.codigo,
      nome: produto.nome,
      imagem: produto.imagem,
      quantidade,
      peso,
      valor,
      total,
      precoUnitario,
      precoMin: parseFloat(produto.precoMin),
      unidade: produto.unidade,
      vendedor: vendedor.vendCodigo,
      cliente: cliente?.cliCodigo,
      ...(carrinho.tipo === 'pedido' && { loja: usandoEstoqueLoja ? 1 : 0, estoque: Number(produto.estoque) }),
    };

    const newItems = new Map(carrinho.items);
    newItems.set(produto.codigo, item);

    // Zera o indicador de previsão enviada ao adicionar novo produto
    if (carrinho.tipo === 'previsao') {
      localStorage.setItem('indPrevisaoEnvio', '0');
    }

    return this.recalculateCarrinho({ ...carrinho, items: newItems });
  }

  /**
   * Remove produto do carrinho
   */
  static removeItem(carrinho: CarrinhoState, codigoProduto: string): CarrinhoState {
    const newItems = new Map(carrinho.items);
    newItems.delete(codigoProduto);

    return this.recalculateCarrinho({ ...carrinho, items: newItems });
  }

  /**
   * Atualiza quantidade de um item
   */
  static updateQuantity(
    carrinho: CarrinhoState,
    codigoProduto: string,
    quantidade: number
  ): CarrinhoState {
    const item = carrinho.items.get(codigoProduto);

    if (!item) {
      throw new Error('Item não encontrado no carrinho');
    }

    if (quantidade <= 0) {
      return this.removeItem(carrinho, codigoProduto);
    }

    const peso = item.peso ? (item.peso / item.quantidade) * quantidade : 0;
    const valor = item.valor || item.precoUnitario || 0; // Mantém o valor unitário
    const total = valor * quantidade; // Total = quantidade * valor

    const updatedItem: ItemCarrinho = {
      ...item,
      quantidade,
      peso,
      valor,
      total,
    };

    const newItems = new Map(carrinho.items);
    newItems.set(codigoProduto, updatedItem);

    return this.recalculateCarrinho({ ...carrinho, items: newItems });
  }

  /**
   * Atualiza preço de um item (apenas pedidos)
   */
  static updatePrice(
    carrinho: CarrinhoState,
    codigoProduto: string,
    novoPreco: number
  ): CarrinhoState {
    const item = carrinho.items.get(codigoProduto);

    if (!item) {
      throw new Error('Item não encontrado no carrinho');
    }

    if (carrinho.tipo !== 'pedido') {
      throw new Error('Edição de preço disponível apenas para pedidos');
    }

    if (item.precoMin && novoPreco < item.precoMin) {
      throw new Error(`Preço não pode ser menor que ${item.precoMin}`);
    }

    const valor = novoPreco; // Valor unitário
    const total = novoPreco * item.quantidade; // Total = quantidade * valor

    const updatedItem: ItemCarrinho = {
      ...item,
      precoUnitario: novoPreco,
      valor,
      total,
    };

    const newItems = new Map(carrinho.items);
    newItems.set(codigoProduto, updatedItem);

    return this.recalculateCarrinho({ ...carrinho, items: newItems });
  }

  /**
   * Recalcula totais do carrinho
   */
  static recalculateCarrinho(carrinho: CarrinhoState): CarrinhoState {
    let total = 0;
    let pesoTotal = 0;
    let quantidadeTotal = 0;

    carrinho.items.forEach((item) => {
      // Para pedidos, usa item.total; para previsão, calcula valor * quantidade
      const itemTotal = item.total || (item.valor || 0) * item.quantidade;
      total += itemTotal;
      pesoTotal += item.peso || 0;
      quantidadeTotal += item.quantidade;
    });

    return {
      ...carrinho,
      total,
      pesoTotal,
      quantidadeTotal,
    };
  }

  /**
   * Limpa carrinho
   */
  static clearCarrinho(tipo: TipoCarrinho): CarrinhoState {
    SessionUtil.remove(SessionKeys.CARRINHO);
    SessionUtil.remove(SessionKeys.CARRINHO_TIPO);
    return this.createEmptyCarrinho(tipo);
  }

  /**
   * Verifica se produto está no carrinho
   */
  static hasItem(carrinho: CarrinhoState, codigoProduto: string): boolean {
    return carrinho.items.has(codigoProduto);
  }

  /**
   * Obtém quantidade de um produto no carrinho
   */
  static getItemQuantity(carrinho: CarrinhoState, codigoProduto: string): number {
    const item = carrinho.items.get(codigoProduto);
    return item?.quantidade || 0;
  }

  /**
   * Obtém total de itens no carrinho
   */
  static getTotalItems(carrinho: CarrinhoState): number {
    return carrinho.items.size;
  }

  /**
   * Verifica se carrinho está vazio
   */
  static isEmpty(carrinho: CarrinhoState): boolean {
    return carrinho.items.size === 0;
  }

  /**
   * Converte carrinho para array de itens
   */
  static toArray(carrinho: CarrinhoState): ItemCarrinho[] {
    return Array.from(carrinho.items.values());
  }

  /**
   * Valida carrinho antes de enviar
   */
  static validateCarrinho(carrinho: CarrinhoState): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (this.isEmpty(carrinho)) {
      errors.push('Carrinho está vazio');
    }

    if (carrinho.tipo === 'pedido') {
      const cliente = SessionUtil.getCliente();
      if (!cliente) {
        errors.push('Cliente não selecionado');
      }

      // Valida preços mínimos
      carrinho.items.forEach((item) => {
        if (item.precoMin && item.precoUnitario && item.precoUnitario < item.precoMin) {
          errors.push(`Preço de ${item.nome} está abaixo do mínimo`);
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Mescla produtos da previsão ativa com carrinho local
   * Se o carrinho já possuir produtos, prevalece o produto + quantidade que existem no carrinho
   */
  static mergePrevisaoProdutos(
    carrinhoAtual: CarrinhoState,
    produtosAPI: ProdutoPrevisaoVendedor[]
  ): CarrinhoState {
    const vendedor = SessionUtil.getVendedor();

    if (!vendedor) {
      console.warn('Vendedor não encontrado na sessão');
      return carrinhoAtual;
    }

    const newItems = new Map(carrinhoAtual.items);

    // Adiciona produtos da API que não estão no carrinho
    produtosAPI.forEach((produtoAPI) => {
      // Se o produto já existe no carrinho local, mantém o que está no carrinho
      if (newItems.has(produtoAPI.codigo)) {
        return;
      }

      // Calcula peso total do item (peso unitário * quantidade)
      const pesoTotal = produtoAPI.peso * produtoAPI.quantidade;

      // Adiciona produto da API ao carrinho com todos os campos
      const item: ItemCarrinho = {
        codigo: produtoAPI.codigo,
        nome: produtoAPI.nome,
        imagem: produtoAPI.imagem,
        unidade: produtoAPI.unidade,
        quantidade: produtoAPI.quantidade,
        peso: pesoTotal,
        vendedor: vendedor.vendCodigo,
      };

      newItems.set(produtoAPI.codigo, item);
    });

    const newCarrinho = { ...carrinhoAtual, items: newItems };
    return this.recalculateCarrinho(newCarrinho);
  }

  /**
   * Calcula a quantidade total de um produto que está usando estoque loja no carrinho
   * Apenas para carrinho tipo 'pedido'
   */
  static getQuantidadeEstoqueLoja(carrinho: CarrinhoState, codigoProduto: string): number {
    if (carrinho.tipo !== 'pedido') {
      return 0;
    }

    const item = carrinho.items.get(codigoProduto);

    // Se o item existe e está usando estoque loja (loja = 1)
    if (item && 'loja' in item && item.loja === 1) {
      return item.quantidade;
    }

    return 0;
  }

  /**
   * Verifica se um produto está usando estoque loja no carrinho
   */
  static isUsandoEstoqueLoja(carrinho: CarrinhoState, codigoProduto: string): boolean {
    if (carrinho.tipo !== 'pedido') {
      return false;
    }

    const item = carrinho.items.get(codigoProduto);
    return item ? ('loja' in item && item.loja === 1) : false;
  }
}